import type { Item as ItemType } from '../../lib/types'
import AuthImage from '../AuthImage'
import LoadingDots from '../LoadingDots'

interface ItemProps {
  item: ItemType
  onClick: () => void
  fullscreen?: boolean
  editMode?: boolean
  onDelete?: () => void
  /** Shows a spinner overlay while this item is being deleted. */
  isDeleting?: boolean
  /** Pre-resolved signed URL for storage-backed items. Falls back to seed data URLs. */
  signedUrl?: string
  /** Called once when this item's image has fully loaded. */
  onLoad?: () => void
}

export default function Item({ item, onClick, fullscreen = false, editMode = false, onDelete, isDeleting = false, signedUrl, onLoad }: ItemProps) {
  // Resolve the display URL: signed URL for storage-backed items, else seed data URL
  const displaySrc = signedUrl ?? (item.storagePath ? undefined : (item.fileType === 'video' ? item.fullUrl : item.thumbnailUrl))

  const handleClick = () => {
    if (editMode) return // suppress viewer in edit mode
    onClick()
  }

  return (
    <div
      onClick={handleClick}
      className={`
        relative
        overflow-hidden
        ${editMode ? 'cursor-default' : 'cursor-pointer'}
        ${fullscreen ? 'w-full h-full' : 'aspect-square'}
      `}
    >
      {/* Always render AuthImage — passing empty string when URL not yet resolved so
          it shows its own skeleton without unmounting/remounting on URL arrival */}
      <AuthImage
        src={displaySrc ?? ''}
        alt=""
        className="absolute inset-0 w-full h-full"
        loading="lazy"
        isVideo={item.fileType === 'video'}
        onLoad={onLoad}
        data-item-id={item.id}
      />

      {item.fileType === 'video' && !editMode && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
          <svg xmlns="http://www.w3.org/2000/svg" fill="white" viewBox="0 0 24 24" className="w-12 h-12">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      )}

      {editMode && onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
          disabled={isDeleting}
          className="
            absolute inset-0 w-full h-full
            bg-black/40
            flex items-center justify-center
            transition-opacity duration-150
            ${isDeleting ? 'cursor-wait' : ''}
          "
          aria-label="Slett bilde"
        >
          {isDeleting ? (
            <LoadingDots />
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="white" className="w-8 h-8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          )}
        </button>
      )}
    </div>
  )
}
